import React, { useState, useEffect } from 'react';
import { Gift, Copy, Check, DollarSign, Loader2 } from 'lucide-react';
import { RevShareData } from '../types';
import { useWallet } from '../contexts/WalletContext';
import revshareService from '../services/revshare.service';

const RevShareSection: React.FC = () => {
  const { isConnected, publicKey } = useWallet();
  const [revShareData, setRevShareData] = useState<RevShareData>({
    referrer: null,
    totalEarnings: 0,
    pendingRewards: 0,
    totalReferrals: 0,
    referralLink: ''
  });
  const [copied, setCopied] = useState(false);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (isConnected && publicKey) {
      const referralLink = `${window.location.origin}?ref=${publicKey}`;
      setRevShareData(prev => ({ ...prev, referralLink }));
      loadEarnings();
    }
  }, [isConnected, publicKey]);

  const loadEarnings = async () => {
    const earnings = await revshareService.getUserEarnings();
    setRevShareData(prev => ({
      ...prev,
      totalEarnings: earnings.totalEarnings,
      pendingRewards: earnings.pendingRewards,
      totalReferrals: earnings.totalReferrals
    }));
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(revShareData.referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const claimEarnings = async () => {
    setClaiming(true);
    try {
      const result = await revshareService.claimEarnings();
      if (result.success) {
        alert(`Successfully claimed ${result.amount.toFixed(4)} SOL!`);
        await loadEarnings();
      }
    } catch (error) {
      console.error('Failed to claim earnings:', error);
    } finally {
      setClaiming(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
        <h2 className="text-2xl font-bold mb-4 flex items-center">
          <Gift className="w-6 h-6 mr-2 text-purple-400" />
          Referral Program
        </h2>
        <p className="text-gray-400">Connect your wallet to access the referral program</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
      <h2 className="text-2xl font-bold mb-6 flex items-center">
        <Gift className="w-6 h-6 mr-2 text-purple-400" />
        Referral Program
      </h2>
      
      <p className="text-gray-400 mb-6">
        Earn 20% commission on all platform fees from users you refer!
      </p>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Your Referral Link</label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={revShareData.referralLink}
              readOnly
              className="flex-1 px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg"
            />
            <button
              onClick={copyReferralLink}
              className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-900/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-400">
              {revShareData.totalEarnings.toFixed(4)}
            </div>
            <div className="text-sm text-gray-400">Total Earnings (SOL)</div>
          </div>
          
          <div className="bg-gray-900/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-400">
              {revShareData.pendingRewards.toFixed(4)}
            </div>
            <div className="text-sm text-gray-400">Pending Rewards (SOL)</div>
          </div>
          
          <div className="bg-gray-900/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-400">
              {revShareData.totalReferrals}
            </div>
            <div className="text-sm text-gray-400">Total Referrals</div>
          </div>
        </div>
        
        <button
          onClick={claimEarnings}
          disabled={claiming || revShareData.pendingRewards === 0}
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          {claiming ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Claiming...</span>
            </>
          ) : (
            <>
              <DollarSign className="w-5 h-5" />
              <span>Claim Earnings</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default RevShareSection;